import { request } from "@/api/client";
import type {
  AgentConfig,
  AgentRun,
  AgentInsight,
  DashboardWidget,
  AgentSchedule,
} from "../types/agent";

// The server bounds the entire operation to ten minutes and persists each run.
export const AGENT_REQUEST_TIMEOUT_MS = 11 * 60 * 1000;

export const agentApi = {
  listAgents: async (): Promise<AgentConfig[]> => {
    return request("get", "/api/agents");
  },

  updateAgentConfig: async (
    agentId: number,
    patch: {
      enabled?: boolean;
      config?: Record<string, unknown>;
      schedule?: string | null;
      name?: string;
    },
  ): Promise<void> => {
    await request("put", `/api/agents/${agentId}/config`, { data: patch });
  },

  runAgent: async (agentId: number): Promise<AgentRun> => {
    return request("post", `/api/agents/${agentId}/run`, {
      timeout: AGENT_REQUEST_TIMEOUT_MS,
    });
  },

  runAllAgents: async (): Promise<{ runs: AgentRun[]; count: number }> => {
    return request("post", "/api/agents/run-all", {
      timeout: AGENT_REQUEST_TIMEOUT_MS,
    });
  },

  listInsights: async (
    filters: {
      category?: string;
      severity?: string;
      acknowledged?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ insights: AgentInsight[]; total: number }> => {
    return request("get", "/api/agents/insights", { params: filters });
  },

  acknowledgeInsight: async (insightId: number): Promise<void> => {
    await request("post", `/api/agents/insights/${insightId}/acknowledge`);
  },

  acknowledgeAllInsights: async (): Promise<number> => {
    const result = await request<{ count: number }>(
      "post",
      "/api/agents/insights/acknowledge-all",
    );
    return result.count;
  },

  getUnreadCount: async (): Promise<number> => {
    const body = await request<{ count: number }>(
      "get",
      "/api/agents/insights/unread-count",
    );
    return body.count;
  },

  listRuns: async (limit?: number): Promise<AgentRun[]> => {
    return request("get", "/api/agents/runs", { params: { limit } });
  },

  listSchedules: async (): Promise<AgentSchedule[]> => {
    return request("get", "/api/agents/schedules");
  },

  updateSchedule: async (
    agentConfigId: number,
    cronExpression: string,
  ): Promise<void> => {
    await request("post", "/api/agents/schedules", {
      data: { agentConfigId, cronExpression },
    });
  },

  initializeAgents: async (companyId: number): Promise<void> => {
    await request("post", `/api/agents/initialize/${companyId}`);
  },

  getPageContext: async (
    page: string,
  ): Promise<{
    page: string;
    data: Record<string, unknown>;
    availableTools: string[];
    fieldHelp: Record<string, string>;
  }> => {
    return request("get", `/api/agents/context/${page}`);
  },
};

export const dashboardWidgetApi = {
  listWidgets: async (): Promise<DashboardWidget[]> => {
    return request("get", "/api/dashboard/widgets");
  },

  addWidget: async (widget: {
    chart_spec: Record<string, unknown>;
    business_unit?: string;
    position?: number;
    size?: string;
  }): Promise<DashboardWidget> => {
    return request("post", "/api/dashboard/widgets", { data: widget });
  },

  updateWidget: async (
    widgetId: number,
    patch: {
      position?: number;
      size?: string;
      chart_spec?: Record<string, unknown>;
    },
  ): Promise<void> => {
    await request("put", `/api/dashboard/widgets/${widgetId}`, { data: patch });
  },

  deleteWidget: async (widgetId: number): Promise<void> => {
    await request("delete", `/api/dashboard/widgets/${widgetId}`);
  },
};
